import { useEffect, useState } from "react";
import { Mail, MoveHorizontal } from "lucide-react";
import { Facebook, MessageCircle } from "lucide-react";
import { api } from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { SectionHeading } from "@/components/Brand";
import BeforeAfter from "@/components/BeforeAfter";

const CONTACT_EMAIL = "hrcreations90@gmail.com";

export const restorationMailto = (lang, title) => {
  const subject = lang === "en" ? "Photo restoration request" : "Solicitud de restauración de fotos";
  const body =
    lang === "en"
      ? `Hello HR Creations,\n\nI would like a quote to restore ${title ? `a photo like "${title}"` : "an old photo"}.\nI can send photos of the damage.\n\nName:\nPhone / WhatsApp:\n`
      : `Hola HR Creations,\n\nQuisiera una cotización para restaurar ${title ? `una foto como “${title}”` : "una foto antigua"}.\nPuedo enviarle fotos del daño.\n\nNombre:\nTeléfono / WhatsApp:\n`;
  return `mailto:${CONTACT_EMAIL}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`;
};

export default function Restoration() {
  const { t, lang, localized } = useI18n();
  const shareUrl = (id) =>
    `${window.location.origin}${window.location.pathname}#${id}`;
  const [items, setItems] = useState([]);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    api.get("/restorations")
      .then((r) => setItems(r.data))
      .catch(() => {})
      .finally(() => setLoaded(true));
  }, []);

  return (
    <div className="mx-auto max-w-[1500px] px-4 py-14 sm:px-8 lg:px-12 lg:py-20" data-testid="restoration-page">
      <SectionHeading label={t("brand.positioning")} title={t("restoration.title")} sub={t("restoration.sub")} />

      <div className="mt-8 flex flex-wrap items-center gap-6">
        <p className="flex items-center gap-2 text-sm text-[#5A5752]" data-testid="restoration-hint">
          <MoveHorizontal size={16} className="text-[#C69A3B]" /> {t("restoration.drag")}
        </p>
        <a
          href={restorationMailto(lang)}
          data-testid="restoration-request-btn"
          className="inline-flex min-h-[48px] items-center gap-2 border border-[#8F3329] bg-[#8F3329] px-6 text-sm text-[#FAFAF7] transition-colors duration-300 hover:bg-[#252525]"
        >
          <Mail size={15} /> {t("restoration.request")}
        </a>
      </div>

      {!loaded ? (
        <p className="mt-12 text-sm text-[#5A5752]" data-testid="restoration-loading">{t("common.loading")}</p>
      ) : items.length === 0 ? (
        <p className="mt-12 text-sm text-[#5A5752]" data-testid="restoration-empty">{t("restoration.empty")}</p>
      ) : (
        <div className="mt-12 grid gap-10 lg:grid-cols-2" data-testid="restoration-grid">
          {items.map((item) => (
            <article key={item.id} id={item.id} data-testid={`restoration-card-${item.id}`}>
              <BeforeAfter
                before={item.before_image}
                after={item.after_image}
                orientation={item.orientation}
                testId={`restoration-compare-${item.id}`}
              />
              <h3 className="mt-5 font-serif-display text-2xl text-[#8F3329]">{localized(item, "title")}</h3>
              {item.year && <p className="label-mono mt-1">{item.year}</p>}
              {localized(item, "notes") && (
                <p className="mt-3 max-w-xl text-sm text-[#5A5752]">{localized(item, "notes")}</p>
              )}
              <div className="mt-4 flex flex-wrap gap-3">
                <a
                  href={`https://wa.me/?text=${encodeURIComponent(
                    `${t("restoration.shareText")} “${localized(item, "title")}” — ${shareUrl(item.id)}`
                  )}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`restoration-share-whatsapp-${item.id}`}
                  className="inline-flex min-h-[42px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#C69A3B] hover:text-[#252525]"
                >
                  <MessageCircle size={14} /> WhatsApp
                </a>
                <a
                  href={`https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(shareUrl(item.id))}`}
                  target="_blank"
                  rel="noreferrer"
                  data-testid={`restoration-share-facebook-${item.id}`}
                  className="inline-flex min-h-[42px] items-center gap-2 border border-[#C69A3B] px-4 text-xs text-[#8F3329] transition-colors duration-300 hover:bg-[#C69A3B] hover:text-[#252525]"
                >
                  <Facebook size={14} /> Facebook
                </a>
              </div>
            </article>
          ))}
        </div>
      )}

      <div className="mt-14 max-w-3xl border border-[#C69A3B]/40 bg-[#FAFAF7] p-7">
        <p className="label-mono">{t("restoration.priceLabel")}</p>
        <p className="mt-3 text-sm text-[#5A5752]">{t("restoration.priceNote")}</p>
        <p className="mt-4 text-sm text-[#5A5752]">
          {t("restoration.note")} <span className="text-[#8F3329]">{CONTACT_EMAIL}</span>
        </p>
      </div>
    </div>
  );
}
